/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, Trash2Icon } from './icons';
import { RetailItem } from '../types';
import { useTranslation } from '../i18n/useTranslation';

interface ShoppingBasketModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: RetailItem[];
  onRemoveItem: (productUrl: string) => void;
}

const ShoppingBasketModal: React.FC<ShoppingBasketModalProps> = ({ isOpen, onClose, items, onRemoveItem }) => {
  const { t } = useTranslation();

  const totalPrice = useMemo(() => {
    return items.reduce((total, item) => {
      // Extract number from price string like "€299.00"
      const priceNumber = parseFloat(item.price.replace(/[^0-9.,]/g, '').replace(',', '.'));
      return total + (isNaN(priceNumber) ? 0 : priceNumber);
    }, 0);
  }, [items]);


  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-serif tracking-wider text-gray-800 dark:text-gray-100">{t('shoppingBasket_title')}</h2>
              <button
                onClick={onClose}
                className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                aria-label={t('common_close')}
                title={t('common_close')}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar flex-grow">
              {items.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('shoppingBasket_empty')}</p>
              ) : (
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <img src={item.imageUrl} alt={item.name} className="w-20 h-24 object-cover rounded-md flex-shrink-0" />
                      <div className="flex-grow">
                        <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-200 line-clamp-2">{item.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.retailer}</p>
                        <p className="font-bold text-gray-900 dark:text-white mt-1">{item.price}</p>
                      </div>
                      <button onClick={() => onRemoveItem(item.productUrl)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title={t('common_remove')}>
                        <Trash2Icon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center font-bold text-lg text-gray-900 dark:text-white mb-4">
                  <span>{t('shoppingBasket_total')}</span>
                  <span>€{totalPrice.toFixed(2)}</span>
                </div>
                <button className="w-full bg-indigo-600 text-white font-semibold py-3 px-5 rounded-lg hover:bg-indigo-700 transition-colors">
                  {t('shoppingBasket_checkout')}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShoppingBasketModal;